import type {
  ExecutionCandlesOverlaysBundle,
  ExecutionCandlesPaneOverlayPoint,
  ExecutionCandlesPriceOverlayPoint,
  TesseraLaneBOverlayId,
} from "../types/executionCandlesOverlays";
import type { ExecutionCandle } from "../types/trade";
import { executionCandleRowTime } from "./executionCandlesNormalize";

type StrategyRdMaKind = "sma" | "ema";

type StrategyRdMaSpec = {
  id: Extract<TesseraLaneBOverlayId, `sma${number}` | `ema${number}`>;
  field: string;
  period: number;
  kind: StrategyRdMaKind;
  title: string;
  color: string;
};

const SMA_PERIODS = [5, 8, 9, 10, 20, 21, 30, 50, 100, 200] as const;
const EMA_PERIODS = [5, 8, 9, 10, 12, 20, 21, 50, 100, 200] as const;

/** Distinct end-label colors — fast/slow pairs stay easy to tell apart. */
const SMA_COLORS: Record<number, string> = {
  5: "#FBBF24",
  8: "#F59E0B",
  9: "#D97706",
  10: "#B45309",
  20: "#A78BFA",
  21: "#8B5CF6",
  30: "#C084FC",
  50: "#F59E0B",
  100: "#7C3AED",
  200: "#8B5CF6",
};

const EMA_COLORS: Record<number, string> = {
  5: "#67E8F9",
  8: "#22D3EE",
  9: "#06B6D4",
  10: "#06B6D4",
  12: "#0891B2",
  20: "#F43F5E",
  21: "#E11D48",
  50: "#FB7185",
  100: "#BE123C",
  200: "#9F1239",
};

function buildMaSpecs(): StrategyRdMaSpec[] {
  const out: StrategyRdMaSpec[] = [];
  for (const period of SMA_PERIODS) {
    const id = `sma${period}` as StrategyRdMaSpec["id"];
    out.push({
      id,
      field: id,
      period,
      kind: "sma",
      title: `SMA ${period}`,
      color: SMA_COLORS[period] || "#F59E0B",
    });
  }
  for (const period of EMA_PERIODS) {
    const id = `ema${period}` as StrategyRdMaSpec["id"];
    out.push({
      id,
      field: id,
      period,
      kind: "ema",
      title: `EMA ${period}`,
      color: EMA_COLORS[period] || "#06B6D4",
    });
  }
  return out;
}

export const STRATEGY_RD_MA_SPECS: readonly StrategyRdMaSpec[] = buildMaSpecs();

const MA_SPEC_BY_ID = new Map(STRATEGY_RD_MA_SPECS.map((s) => [s.id, s]));

/** Resolve Lane B id for an SMA/EMA period (catalog lengths only). */
export function strategyRdMaLaneId(
  kind: StrategyRdMaKind,
  period: number
): TesseraLaneBOverlayId | null {
  if (!Number.isFinite(period) || period <= 0) return null;
  const id = `${kind}${Math.round(period)}`;
  return MA_SPEC_BY_ID.has(id as StrategyRdMaSpec["id"])
    ? (id as TesseraLaneBOverlayId)
    : null;
}

function finiteParamPeriod(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n);
}

const OSC_LANE_IDS: readonly TesseraLaneBOverlayId[] = [
  "rsi",
  "macd",
  "stoch",
  "mfi",
  "adx",
  "atr",
  "bb",
];

function addMaFromConceptId(id: string, into: Set<TesseraLaneBOverlayId>): void {
  if (id === "sma_cross") {
    into.add("sma50");
    into.add("sma200");
    return;
  }
  if (id === "ema_cross") {
    into.add("ema10");
    into.add("ema20");
    return;
  }
  if (id === "pullback_to_ma") {
    into.add("ema10");
    return;
  }
  if (/ema[\s_-]*200|\bema200\b/.test(id)) {
    into.add("ema200");
    return;
  }
  if (/ema[\s_-]*100|\bema100\b/.test(id)) {
    into.add("ema100");
    return;
  }
  if (/ema[\s_-]*50|\bema50\b/.test(id)) {
    into.add("ema50");
    return;
  }
  if (/ema[\s_-]*21|\bema21\b/.test(id)) {
    into.add("ema21");
    return;
  }
  if (/ema[\s_-]*20|\bema20\b/.test(id)) {
    into.add("ema20");
    return;
  }
  if (/ema[\s_-]*12|\bema12\b/.test(id)) {
    into.add("ema12");
    return;
  }
  if (/ema[\s_-]*10|\bema10\b/.test(id)) {
    into.add("ema10");
    return;
  }
  if (/ema[\s_-]*9|\bema9\b/.test(id)) {
    into.add("ema9");
    return;
  }
  if (/ema[\s_-]*8|\bema8\b/.test(id)) {
    into.add("ema8");
    return;
  }
  if (/ema[\s_-]*5|\bema5\b/.test(id)) {
    into.add("ema5");
    return;
  }
  if (/sma[\s_-]*200|\bma200\b/.test(id)) {
    into.add("sma200");
    return;
  }
  if (/sma[\s_-]*100|\bma100\b/.test(id)) {
    into.add("sma100");
    return;
  }
  if (/sma[\s_-]*50|\bma50\b/.test(id)) {
    into.add("sma50");
    return;
  }
  if (/sma[\s_-]*21|\bma21\b/.test(id)) {
    into.add("sma21");
    return;
  }
  if (/sma[\s_-]*20|\bma20\b/.test(id)) {
    into.add("sma20");
    return;
  }
  if (/sma[\s_-]*10|\bma10\b/.test(id)) {
    into.add("sma10");
    return;
  }
  if (/sma[\s_-]*9|\bma9\b/.test(id)) {
    into.add("sma9");
    return;
  }
  if (/sma[\s_-]*8|\bma8\b/.test(id)) {
    into.add("sma8");
    return;
  }
  if (/sma[\s_-]*5|\bma5\b/.test(id)) {
    into.add("sma5");
    return;
  }
  // Family chips without a period — default chart lengths.
  if (id === "sma" || id === "above_sma" || id === "below_sma") {
    into.add("sma50");
    return;
  }
  if (id === "ema" || id === "above_ema" || id === "below_ema") {
    into.add("ema20");
  }
}

/** Concepts that should paint as real chart indicators, not one-bar labels. */
export function strategyRdLaneBFromConceptIds(
  ids: readonly string[] | null | undefined
): TesseraLaneBOverlayId[] {
  const into = new Set<TesseraLaneBOverlayId>();
  for (const raw of ids ?? []) {
    const id = raw.trim().toLowerCase();
    if (!id) continue;
    addMaFromConceptId(id, into);
    if (/(^|_)rsi($|_)/.test(id) || id.includes("rsi_div")) {
      into.add("rsi");
      continue;
    }
    if (
      /(^|_)macd($|_)/.test(id) ||
      id.includes("macd_div") ||
      id.includes("macd_cross") ||
      id.includes("macd_zero")
    ) {
      into.add("macd");
      continue;
    }
    if (/(^|_)stoch($|_)/.test(id) || id.includes("stoch_div")) {
      into.add("stoch");
      continue;
    }
    if (/(^|_)mfi($|_)/.test(id)) {
      into.add("mfi");
      continue;
    }
    if (/(^|_)adx($|_)/.test(id)) {
      into.add("adx");
      continue;
    }
    if (id.includes("atr_regime") || /(^|_)atr($|_)/.test(id)) {
      into.add("atr");
      continue;
    }
    if (
      id.includes("bb_zone") ||
      id.includes("bb_close") ||
      id.includes("bb_squeeze") ||
      id.includes("ttm_squeeze") ||
      /(^|_)bb($|_)/.test(id)
    ) {
      into.add("bb");
    }
  }
  return Array.from(into);
}

/**
 * Lane B overlays from setup/confluence recipe params (fast/slow/length).
 * Prefer params over bare concept defaults for crosses and family MAs.
 */
export function strategyRdLaneBFromRecipeSlots(input: {
  setup?: { id?: string | null; params?: Record<string, unknown> | null } | null;
  confluences?: readonly {
    id?: string | null;
    params?: Record<string, unknown> | null;
  }[] | null;
}): TesseraLaneBOverlayId[] {
  const into = new Set<TesseraLaneBOverlayId>();
  const setupId = (input.setup?.id || "").trim().toLowerCase();
  const setupParams = input.setup?.params;

  if (setupId === "sma_cross" || setupId === "ema_cross") {
    const kind: StrategyRdMaKind = setupId === "ema_cross" ? "ema" : "sma";
    const fastDefault = kind === "ema" ? 10 : 50;
    const slowDefault = kind === "ema" ? 20 : 200;
    const fast = finiteParamPeriod(setupParams?.fast) ?? fastDefault;
    const slow = finiteParamPeriod(setupParams?.slow) ?? slowDefault;
    const fastId = strategyRdMaLaneId(kind, fast);
    const slowId = strategyRdMaLaneId(kind, slow);
    if (fastId) into.add(fastId);
    if (slowId) into.add(slowId);
  } else if (setupId === "pullback_to_ma") {
    const kindRaw = String(setupParams?.ma_kind ?? "ema").trim().toLowerCase();
    const kind: StrategyRdMaKind = kindRaw === "sma" ? "sma" : "ema";
    const length = finiteParamPeriod(setupParams?.ma_length) ?? 10;
    const lane = strategyRdMaLaneId(kind, length);
    if (lane) into.add(lane);
  } else if (
    setupId === "stage" ||
    setupId === "stage_2" ||
    setupId === "stage_4" ||
    /^stage_\d+$/.test(setupId)
  ) {
    const length = finiteParamPeriod(setupParams?.ma_length) ?? 30;
    const lane = strategyRdMaLaneId("sma", length);
    if (lane) into.add(lane);
  } else if (setupId) {
    for (const id of strategyRdLaneBFromConceptIds([setupId])) into.add(id);
  }

  for (const slot of input.confluences ?? []) {
    const id = (slot.id || "").trim().toLowerCase();
    if (!id) continue;
    const length = finiteParamPeriod(slot.params?.length);
    if (length != null && (id === "sma" || /(^|_)sma($|_)/.test(id)) && !id.includes("cross")) {
      const lane = strategyRdMaLaneId("sma", length);
      if (lane) {
        into.add(lane);
        continue;
      }
    }
    if (length != null && (id === "ema" || /(^|_)ema($|_)/.test(id)) && !id.includes("cross")) {
      const lane = strategyRdMaLaneId("ema", length);
      if (lane) {
        into.add(lane);
        continue;
      }
    }
    for (const lane of strategyRdLaneBFromConceptIds([id])) into.add(lane);
  }

  return Array.from(into);
}

export function strategyRdConceptUsesChartIndicator(id: string | null | undefined): boolean {
  const key = (id || "").trim().toLowerCase();
  if (!key) return false;
  return strategyRdLaneBFromConceptIds([key]).length > 0;
}

export function strategyRdConceptUsesOscillatorPane(id: string | null | undefined): boolean {
  const lanes = strategyRdLaneBFromConceptIds([id || ""]);
  return lanes.some((x) => OSC_LANE_IDS.includes(x) && x !== "bb");
}

export function simpleMovingAverage(
  values: readonly number[],
  period: number
): Array<number | null> {
  if (period <= 0) return values.map(() => null);
  const out: Array<number | null> = [];
  let sum = 0;
  for (let i = 0; i < values.length; i += 1) {
    const v = values[i]!;
    sum += v;
    if (i >= period) sum -= values[i - period]!;
    out.push(i >= period - 1 && Number.isFinite(sum) ? sum / period : null);
  }
  return out;
}

/** SMA-seeded EMA — skips leading nulls (needed for EMA of MACD line → signal). */
export function exponentialMovingAverage(
  values: readonly (number | null)[],
  period: number
): Array<number | null> {
  if (period <= 0) return values.map(() => null);
  const k = 2 / (period + 1);
  const out: Array<number | null> = values.map(() => null);
  let start: number | null = null;
  for (let i = period - 1; i < values.length; i += 1) {
    let ok = true;
    for (let j = i - period + 1; j <= i; j += 1) {
      if (values[j] == null || !Number.isFinite(values[j]!)) {
        ok = false;
        break;
      }
    }
    if (!ok) continue;
    start = i;
    break;
  }
  if (start == null) return out;
  let seedSum = 0;
  for (let j = start - period + 1; j <= start; j += 1) seedSum += values[j]!;
  let ema: number | null = seedSum / period;
  out[start] = ema;
  for (let i = start + 1; i < values.length; i += 1) {
    const v = values[i];
    if (v == null || !Number.isFinite(v) || ema == null) {
      out[i] = null;
      ema = null;
      continue;
    }
    ema = v * k + ema * (1 - k);
    out[i] = Number.isFinite(ema) ? ema : null;
  }
  return out;
}

/** Wilder RSI. */
export function relativeStrengthIndex(
  closes: readonly number[],
  period = 14
): Array<number | null> {
  const out: Array<number | null> = closes.map(() => null);
  if (period <= 0 || closes.length <= period) return out;
  let avgGain = 0;
  let avgLoss = 0;
  for (let i = 1; i <= period; i += 1) {
    const diff = closes[i]! - closes[i - 1]!;
    if (diff >= 0) avgGain += diff;
    else avgLoss -= diff;
  }
  avgGain /= period;
  avgLoss /= period;
  out[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  for (let i = period + 1; i < closes.length; i += 1) {
    const diff = closes[i]! - closes[i - 1]!;
    const gain = diff > 0 ? diff : 0;
    const loss = diff < 0 ? -diff : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    out[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }
  return out;
}

export function macdSeries(
  closes: readonly number[],
  fast = 12,
  slow = 26,
  signalPeriod = 9
): {
  macd: Array<number | null>;
  signal: Array<number | null>;
  hist: Array<number | null>;
} {
  const fastEma = exponentialMovingAverage(closes, fast);
  const slowEma = exponentialMovingAverage(closes, slow);
  const macd = closes.map((_, i) => {
    const f = fastEma[i];
    const s = slowEma[i];
    if (f == null || s == null) return null;
    return f - s;
  });
  // Signal = EMA of MACD line — must skip leading nulls (same as BE macd_series).
  const signal = exponentialMovingAverage(macd, signalPeriod);
  const hist = macd.map((v, i) => {
    const s = signal[i];
    if (v == null || s == null) return null;
    return v - s;
  });
  return { macd, signal, hist };
}

export function stochasticKd(
  highs: readonly number[],
  lows: readonly number[],
  closes: readonly number[],
  length = 14,
  smoothK = 3,
  smoothD = 3
): { k: Array<number | null>; d: Array<number | null> } {
  const rawK: Array<number | null> = closes.map(() => null);
  for (let i = 0; i < closes.length; i += 1) {
    if (i < length - 1) continue;
    let hh = Number.NEGATIVE_INFINITY;
    let ll = Number.POSITIVE_INFINITY;
    for (let j = i - length + 1; j <= i; j += 1) {
      hh = Math.max(hh, highs[j]!);
      ll = Math.min(ll, lows[j]!);
    }
    const range = hh - ll;
    rawK[i] = range <= 0 ? 50 : ((closes[i]! - ll) / range) * 100;
  }
  const k = simpleMovingAverage(
    rawK.map((v) => (v == null ? 0 : v)),
    Math.max(1, smoothK)
  ).map((v, i) => (rawK[i] == null ? null : v));
  const d = simpleMovingAverage(
    k.map((v) => (v == null ? 0 : v)),
    Math.max(1, smoothD)
  ).map((v, i) => (k[i] == null ? null : v));
  return { k, d };
}

export function moneyFlowIndex(
  highs: readonly number[],
  lows: readonly number[],
  closes: readonly number[],
  volumes: ReadonlyArray<number | null>,
  period = 14
): Array<number | null> {
  const out: Array<number | null> = closes.map(() => null);
  const tp = closes.map((c, i) => (highs[i]! + lows[i]! + c) / 3);
  const rawMf = tp.map((t, i) => {
    const vol = volumes[i];
    if (vol == null || !Number.isFinite(vol) || vol <= 0) return null;
    return t * vol;
  });
  for (let i = period; i < closes.length; i += 1) {
    let pos = 0;
    let neg = 0;
    let ok = true;
    for (let j = i - period + 1; j <= i; j += 1) {
      const mf = rawMf[j];
      if (mf == null) {
        ok = false;
        break;
      }
      const prev = tp[j - 1]!;
      if (tp[j]! > prev) pos += mf;
      else if (tp[j]! < prev) neg += mf;
    }
    if (!ok) continue;
    out[i] = neg === 0 ? 100 : 100 - 100 / (1 + pos / neg);
  }
  return out;
}

export function atrWilder(
  highs: readonly number[],
  lows: readonly number[],
  closes: readonly number[],
  period = 14
): Array<number | null> {
  const out: Array<number | null> = closes.map(() => null);
  if (closes.length <= period) return out;
  const tr: number[] = [highs[0]! - lows[0]!];
  for (let i = 1; i < closes.length; i += 1) {
    const hl = highs[i]! - lows[i]!;
    const hc = Math.abs(highs[i]! - closes[i - 1]!);
    const lc = Math.abs(lows[i]! - closes[i - 1]!);
    tr.push(Math.max(hl, hc, lc));
  }
  let atr = 0;
  for (let i = 0; i < period; i += 1) atr += tr[i]!;
  atr /= period;
  out[period - 1] = atr;
  for (let i = period; i < closes.length; i += 1) {
    atr = (atr * (period - 1) + tr[i]!) / period;
    out[i] = atr;
  }
  return out;
}

/** Simplified ADX (+DI/−DI/ADX) — Wilder. */
export function adxSeries(
  highs: readonly number[],
  lows: readonly number[],
  closes: readonly number[],
  period = 14
): {
  adx: Array<number | null>;
  plusDi: Array<number | null>;
  minusDi: Array<number | null>;
} {
  const n = closes.length;
  const adx: Array<number | null> = Array(n).fill(null);
  const plusDi: Array<number | null> = Array(n).fill(null);
  const minusDi: Array<number | null> = Array(n).fill(null);
  if (n <= period * 2) return { adx, plusDi, minusDi };

  const tr: number[] = [0];
  const plusDm: number[] = [0];
  const minusDm: number[] = [0];
  for (let i = 1; i < n; i += 1) {
    const up = highs[i]! - highs[i - 1]!;
    const down = lows[i - 1]! - lows[i]!;
    plusDm.push(up > down && up > 0 ? up : 0);
    minusDm.push(down > up && down > 0 ? down : 0);
    const hl = highs[i]! - lows[i]!;
    const hc = Math.abs(highs[i]! - closes[i - 1]!);
    const lc = Math.abs(lows[i]! - closes[i - 1]!);
    tr.push(Math.max(hl, hc, lc));
  }

  let atr = 0;
  let pDm = 0;
  let mDm = 0;
  for (let i = 1; i <= period; i += 1) {
    atr += tr[i]!;
    pDm += plusDm[i]!;
    mDm += minusDm[i]!;
  }
  const dx: Array<number | null> = Array(n).fill(null);
  for (let i = period; i < n; i += 1) {
    if (i > period) {
      atr = atr - atr / period + tr[i]!;
      pDm = pDm - pDm / period + plusDm[i]!;
      mDm = mDm - mDm / period + minusDm[i]!;
    }
    const pdi = atr === 0 ? 0 : (100 * pDm) / atr;
    const mdi = atr === 0 ? 0 : (100 * mDm) / atr;
    plusDi[i] = pdi;
    minusDi[i] = mdi;
    const sum = pdi + mdi;
    dx[i] = sum === 0 ? 0 : (100 * Math.abs(pdi - mdi)) / sum;
  }
  let adxAcc = 0;
  let counted = 0;
  for (let i = period; i < n; i += 1) {
    const v = dx[i];
    if (v == null) continue;
    if (counted < period) {
      adxAcc += v;
      counted += 1;
      if (counted === period) adx[i] = adxAcc / period;
    } else {
      adxAcc = (adxAcc * (period - 1) + v) / period;
      adx[i] = adxAcc;
    }
  }
  return { adx, plusDi, minusDi };
}

export function bollingerBands(
  closes: readonly number[],
  period = 20,
  mult = 2
): {
  upper: Array<number | null>;
  mid: Array<number | null>;
  lower: Array<number | null>;
} {
  const mid = simpleMovingAverage(closes, period);
  const upper: Array<number | null> = [];
  const lower: Array<number | null> = [];
  for (let i = 0; i < closes.length; i += 1) {
    const m = mid[i];
    if (m == null || i < period - 1) {
      upper.push(null);
      lower.push(null);
      continue;
    }
    let sumSq = 0;
    for (let j = i - period + 1; j <= i; j += 1) {
      const d = closes[j]! - m;
      sumSq += d * d;
    }
    const sd = Math.sqrt(sumSq / period);
    upper.push(m + mult * sd);
    lower.push(m - mult * sd);
  }
  return { upper, mid, lower };
}

function seriesHasFinite(
  series: Array<ExecutionCandlesPriceOverlayPoint> | null | undefined,
  key: string
): boolean {
  return Boolean(series?.some((p) => {
    const v = p[key];
    return v != null && typeof v !== "string" && Number.isFinite(Number(v));
  }));
}

function paneHasFinite(
  series: Array<ExecutionCandlesPaneOverlayPoint> | null | undefined,
  key: keyof ExecutionCandlesPaneOverlayPoint
): boolean {
  return Boolean(series?.some((p) => {
    const v = p[key];
    return v != null && typeof v !== "string" && Number.isFinite(Number(v));
  }));
}

function finiteOrNull(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Lab preview has OHLC but no Trade Review include_overlays.
 * Fill selected SMA/EMA/BB series from closes when Lane B ids are on and BE series is missing.
 */
export function mergeComputedPriceOverlays(
  candles: readonly ExecutionCandle[] | null | undefined,
  overlays: ExecutionCandlesOverlaysBundle | null | undefined,
  enabled: ReadonlySet<TesseraLaneBOverlayId>
): ExecutionCandlesOverlaysBundle | null {
  const wantedMa = STRATEGY_RD_MA_SPECS.filter((spec) => enabled.has(spec.id));
  const wantBb = enabled.has("bb");
  if (!wantedMa.length && !wantBb) return overlays ?? null;

  const existing = overlays?.price?.series ?? null;
  const missingMa = wantedMa.filter((spec) => !seriesHasFinite(existing, spec.field));
  const missingBb =
    wantBb &&
    !(
      seriesHasFinite(existing, "bb_upper") &&
      seriesHasFinite(existing, "bb_middle") &&
      seriesHasFinite(existing, "bb_lower")
    );
  if (!missingMa.length && !missingBb) return overlays ?? null;

  const list = candles ?? [];
  const closes = list.map((c) => Number(c.close));
  const computed: Partial<Record<string, Array<number | null>>> = {};
  missingMa.forEach((spec) => {
    computed[spec.field] =
      spec.kind === "ema"
        ? exponentialMovingAverage(closes, spec.period)
        : simpleMovingAverage(closes, spec.period);
  });
  const bb = missingBb ? bollingerBands(closes, 20, 2) : null;

  const series = list.map((c, i) => {
    const prior = existing?.[i];
    const point: ExecutionCandlesPriceOverlayPoint = {
      time: executionCandleRowTime(c) || prior?.time || null,
      bb_upper: prior?.bb_upper ?? null,
      bb_middle: prior?.bb_middle ?? null,
      bb_lower: prior?.bb_lower ?? null,
    };
    // Preserve any prior MA fields, then fill enabled ones.
    for (const spec of STRATEGY_RD_MA_SPECS) {
      const priorVal = prior?.[spec.field];
      if (priorVal != null && priorVal !== "" && Number.isFinite(Number(priorVal))) {
        point[spec.field] = Number(priorVal);
      }
    }
    wantedMa.forEach((spec) => {
      if (seriesHasFinite(existing, spec.field)) {
        point[spec.field] = finiteOrNull(prior?.[spec.field]) ?? prior?.[spec.field] ?? null;
        return;
      }
      point[spec.field] = finiteOrNull(computed[spec.field]?.[i]);
    });
    if (bb) {
      point.bb_upper = finiteOrNull(bb.upper[i]);
      point.bb_middle = finiteOrNull(bb.mid[i]);
      point.bb_lower = finiteOrNull(bb.lower[i]);
    }
    return point;
  });

  return {
    ...(overlays ?? {}),
    price: {
      ...(overlays?.price ?? {}),
      series,
    },
  };
}

/**
 * FE-local oscillator panes when BE pane series is missing (Strategy Lab).
 * Periods: prefer echoed match/recipe params so osc_line Y aligns with the pane.
 */
export type StrategyRdOscComputeParams = {
  macd?: { fast?: number | null; slow?: number | null; signal?: number | null };
  rsi?: { period?: number | null };
  stoch?: {
    length?: number | null;
    smoothK?: number | null;
    smoothD?: number | null;
  };
};

/** Pull osc periods from match meta / evidence (BE echoes fast/slow/signal/length). */
export function strategyRdOscParamsFromMeta(
  meta: Record<string, unknown> | null | undefined
): StrategyRdOscComputeParams {
  if (!meta) return {};
  const fast = finiteParamPeriod(meta.fast);
  const slow = finiteParamPeriod(meta.slow);
  const signal = finiteParamPeriod(meta.signal);
  const period =
    finiteParamPeriod(meta.period) ?? finiteParamPeriod(meta.length);
  const length = finiteParamPeriod(meta.length) ?? finiteParamPeriod(meta.period);
  const smoothK =
    finiteParamPeriod(meta.smooth_k) ?? finiteParamPeriod(meta.smooth);
  const smoothD =
    finiteParamPeriod(meta.smooth_d) ?? finiteParamPeriod(meta.smooth);
  const out: StrategyRdOscComputeParams = {};
  if (fast != null || slow != null || signal != null) {
    out.macd = { fast, slow, signal };
  }
  if (period != null) out.rsi = { period };
  if (length != null || smoothK != null || smoothD != null) {
    out.stoch = { length, smoothK, smoothD };
  }
  return out;
}

/** Merge recipe slot params (setup + confluences) for local osc compute. */
export function strategyRdOscParamsFromRecipe(input: {
  setup?: { id?: string | null; params?: Record<string, unknown> | null } | null;
  confluences?: readonly {
    id?: string | null;
    params?: Record<string, unknown> | null;
  }[] | null;
  matchMeta?: Record<string, unknown> | null;
}): StrategyRdOscComputeParams {
  const fromMeta = strategyRdOscParamsFromMeta(input.matchMeta);
  const slots: Array<Record<string, unknown> | null | undefined> = [
    input.setup?.params,
    ...(input.confluences ?? []).map((c) => c.params),
  ];
  let macd = fromMeta.macd ? { ...fromMeta.macd } : undefined;
  let rsi = fromMeta.rsi ? { ...fromMeta.rsi } : undefined;
  let stoch = fromMeta.stoch ? { ...fromMeta.stoch } : undefined;
  for (const params of slots) {
    if (!params) continue;
    const fast = finiteParamPeriod(params.fast);
    const slow = finiteParamPeriod(params.slow);
    const signal = finiteParamPeriod(params.signal);
    if (fast != null || slow != null || signal != null) {
      macd = {
        fast: fast ?? macd?.fast,
        slow: slow ?? macd?.slow,
        signal: signal ?? macd?.signal,
      };
    }
    const period =
      finiteParamPeriod(params.period) ?? finiteParamPeriod(params.length);
    if (period != null) rsi = { period };
    const length =
      finiteParamPeriod(params.length) ?? finiteParamPeriod(params.period);
    const smoothK =
      finiteParamPeriod(params.smooth_k) ?? finiteParamPeriod(params.smooth);
    const smoothD =
      finiteParamPeriod(params.smooth_d) ?? finiteParamPeriod(params.smooth);
    if (length != null || smoothK != null || smoothD != null) {
      stoch = {
        length: length ?? stoch?.length,
        smoothK: smoothK ?? stoch?.smoothK,
        smoothD: smoothD ?? stoch?.smoothD,
      };
    }
  }
  return { macd, rsi, stoch };
}

export function mergeComputedPaneOverlays(
  candles: readonly ExecutionCandle[] | null | undefined,
  overlays: ExecutionCandlesOverlaysBundle | null | undefined,
  enabled: ReadonlySet<TesseraLaneBOverlayId>,
  oscParams?: StrategyRdOscComputeParams | null
): ExecutionCandlesOverlaysBundle | null {
  const wantRsi = enabled.has("rsi");
  const wantMacd = enabled.has("macd");
  const wantStoch = enabled.has("stoch");
  const wantMfi = enabled.has("mfi");
  const wantAdx = enabled.has("adx");
  const wantAtr = enabled.has("atr");
  if (!wantRsi && !wantMacd && !wantStoch && !wantMfi && !wantAdx && !wantAtr) {
    return overlays ?? null;
  }

  const existing = overlays?.panes?.series ?? null;
  const needRsi = wantRsi && !paneHasFinite(existing, "rsi");
  const needMacd = wantMacd && !paneHasFinite(existing, "macd");
  const needStoch = wantStoch && !paneHasFinite(existing, "stoch_k");
  const needMfi = wantMfi && !paneHasFinite(existing, "mfi");
  const needAdx = wantAdx && !paneHasFinite(existing, "adx");
  const needAtr = wantAtr && !paneHasFinite(existing, "atr") && !paneHasFinite(existing, "atr_pct");
  if (!needRsi && !needMacd && !needStoch && !needMfi && !needAdx && !needAtr) {
    return overlays ?? null;
  }

  const list = candles ?? [];
  const closes = list.map((c) => Number(c.close));
  const highs = list.map((c) => Number(c.high));
  const lows = list.map((c) => Number(c.low));
  const volumes = list.map((c) => {
    const v = c.volume;
    if (v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  });

  const rsiPeriod = oscParams?.rsi?.period ?? 14;
  const macdFast = oscParams?.macd?.fast ?? 12;
  const macdSlow = oscParams?.macd?.slow ?? 26;
  const macdSignal = oscParams?.macd?.signal ?? 9;
  const stochLength = oscParams?.stoch?.length ?? 14;
  const stochSmoothK = oscParams?.stoch?.smoothK ?? 3;
  const stochSmoothD = oscParams?.stoch?.smoothD ?? 3;

  const rsi = needRsi ? relativeStrengthIndex(closes, rsiPeriod) : null;
  const macd = needMacd
    ? macdSeries(closes, macdFast, macdSlow, macdSignal)
    : null;
  const stoch = needStoch
    ? stochasticKd(highs, lows, closes, stochLength, stochSmoothK, stochSmoothD)
    : null;
  const mfi = needMfi ? moneyFlowIndex(highs, lows, closes, volumes, 14) : null;
  const adx = needAdx ? adxSeries(highs, lows, closes, 14) : null;
  const atr = needAtr ? atrWilder(highs, lows, closes, 14) : null;

  const series = list.map((c, i) => {
    const prior = existing?.[i];
    const atrVal = atr ? atr[i] : null;
    const close = closes[i]!;
    const point: ExecutionCandlesPaneOverlayPoint = {
      time: executionCandleRowTime(c) || prior?.time || null,
      adx: prior?.adx ?? null,
      plus_di: prior?.plus_di ?? null,
      minus_di: prior?.minus_di ?? null,
      rsi: prior?.rsi ?? null,
      atr: prior?.atr ?? null,
      atr_pct: prior?.atr_pct ?? null,
      macd: prior?.macd ?? null,
      macd_signal: prior?.macd_signal ?? null,
      macd_hist: prior?.macd_hist ?? null,
      stoch_k: prior?.stoch_k ?? null,
      stoch_d: prior?.stoch_d ?? null,
      mfi: prior?.mfi ?? null,
    };
    if (rsi) point.rsi = finiteOrNull(rsi[i]);
    if (macd) {
      point.macd = finiteOrNull(macd.macd[i]);
      point.macd_signal = finiteOrNull(macd.signal[i]);
      point.macd_hist = finiteOrNull(macd.hist[i]);
    }
    if (stoch) {
      point.stoch_k = finiteOrNull(stoch.k[i]);
      point.stoch_d = finiteOrNull(stoch.d[i]);
    }
    if (mfi) point.mfi = finiteOrNull(mfi[i]);
    if (adx) {
      point.adx = finiteOrNull(adx.adx[i]);
      point.plus_di = finiteOrNull(adx.plusDi[i]);
      point.minus_di = finiteOrNull(adx.minusDi[i]);
    }
    if (atr) {
      point.atr = finiteOrNull(atrVal);
      point.atr_pct =
        atrVal != null && close !== 0 ? finiteOrNull((atrVal / Math.abs(close)) * 100) : null;
    }
    return point;
  });

  return {
    ...(overlays ?? {}),
    panes: {
      ...(overlays?.panes ?? {}),
      series,
    },
  };
}

/** Price + pane FE-local fill for Strategy Lab. */
export function mergeComputedStrategyRdOverlays(
  candles: readonly ExecutionCandle[] | null | undefined,
  overlays: ExecutionCandlesOverlaysBundle | null | undefined,
  enabled: ReadonlySet<TesseraLaneBOverlayId>,
  oscParams?: StrategyRdOscComputeParams | null
): ExecutionCandlesOverlaysBundle | null {
  const withPrice = mergeComputedPriceOverlays(candles, overlays, enabled);
  return mergeComputedPaneOverlays(candles, withPrice, enabled, oscParams);
}

